import { Text, Spinner, SpinnerSize } from "@fluentui/react";
import { useState, useEffect } from "react";
import { capitalizeFirstLetter } from "../../utils/textUtils";
import { ComparisonFAB } from "../CarComparison/ComparisonFAB";
import { CarComparisonView } from "../CarComparison/CarComparisonView";
import { CarListing } from "../CarComparison/types";
import { savedCarsService, SavedCarWithDetails } from "../../api/savedCarsService";
import styles from "./SavedCars.module.css";

interface SavedCarsProps {
    onSelectCar: (car: CarListing) => void;
    onClose?: () => void;
}

export const SavedCars = ({ onSelectCar, onClose }: SavedCarsProps) => {
    const [savedCars, setSavedCars] = useState<CarListing[]>([]);
    const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isRemoving, setIsRemoving] = useState<string | null>(null);
    const [previewCar, setPreviewCar] = useState<CarListing | null>(null);
    const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
    const [imageError, setImageError] = useState<Record<string, boolean>>({});
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        loadSavedCars();
    }, []);

    // Auto-select first car for preview on desktop when cars load
    useEffect(() => {
        if (savedCars.length > 0 && !previewCar) {
            setPreviewCar(savedCars[0]);
        }
    }, [savedCars, previewCar]);

    const loadSavedCars = async () => {
        setIsLoading(true);
        try {
            const savedCarsWithDetails = await savedCarsService.listSavedCars();
            const cars: CarListing[] = savedCarsWithDetails
                .filter((item: SavedCarWithDetails) => item.car !== null)
                .map((item: SavedCarWithDetails) => ({
                    ...item.car!,
                    savedAt: item.saved_at,
                }));
            setSavedCars(cars);
        } catch (error) {
            console.error("Error loading saved cars:", error);
            setSavedCars([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveCar = async (carId: string, e: React.MouseEvent<any>) => {
        e.stopPropagation();
        if (isRemoving) return;

        setIsRemoving(carId);
        try {
            await savedCarsService.unsaveCar(carId);
            setSavedCars(prevCars => {
                const newCars = prevCars.filter(car => car.id_unico !== carId);
                // If we removed the currently previewed car, select the next one
                if (previewCar?.id_unico === carId) {
                    setPreviewCar(newCars.length > 0 ? newCars[0] : null);
                }
                return newCars;
            });
            window.dispatchEvent(new Event('savedCarsUpdated'));
        } catch (error) {
            console.error("Error removing saved car:", error);
        } finally {
            setIsRemoving(null);
        }
    };

    const formatSavedDate = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return 'Hoy';
        if (diffDays === 0) return 'Hoy';
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `${diffDays} días`;
        if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return `${weeks} sem`;
        }
        const months = Math.floor(diffDays / 30);
        return `${months} mes${months > 1 ? 'es' : ''}`;
    };

    const handleToggleSelection = (carId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedForComparison(prev => {
            if (prev.includes(carId)) {
                return prev.filter(id => id !== carId);
            }
            if (prev.length >= 4) return prev;
            return [...prev, carId];
        });
    };

    const handleClearSelection = () => {
        setSelectedForComparison([]);
    };

    const handleOpenComparison = () => {
        setIsComparisonOpen(true);
    };

    const handleRemoveFromComparison = (carId: string) => {
        setSelectedForComparison(prev => prev.filter(id => id !== carId));
    };

    const handleCarClick = (car: CarListing) => {
        setPreviewCar(car);
        // On mobile, also open the preview panel
        if (window.innerWidth <= 768) {
            setIsMobilePreviewOpen(true);
        }
    };

    const handleMobileBack = () => {
        setIsMobilePreviewOpen(false);
    };

    const handleViewFullDetails = () => {
        if (previewCar) {
            onSelectCar(previewCar);
        }
    };

    const selectedCarsData = selectedForComparison
        .map(id => savedCars.find(car => car.id_unico === id))
        .filter(Boolean) as CarListing[];

    // Calculate stats
    const totalValue = savedCars.reduce((sum, car) => sum + (car.precio || 0), 0);
    const avgPrice = savedCars.length > 0 ? totalValue / savedCars.length : 0;

    // Reset image index when preview car changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [previewCar?.id_unico]);

    // Get all available images for a car
    const getCarImages = (car: CarListing): string[] => {
        const images: string[] = [];
        if (car.imagen_url) images.push(car.imagen_url);
        if (car.image_url_2) images.push(car.image_url_2);
        if (car.image_url_3) images.push(car.image_url_3);
        if (car.image_url_4) images.push(car.image_url_4);
        if (car.image_url_5) images.push(car.image_url_5);
        return images;
    };

    // Handle image error
    const handleImageError = (imageUrl: string) => {
        setImageError(prev => ({ ...prev, [imageUrl]: true }));
    };

    // Get price indicator for preview
    const getPriceIndicator = (car: CarListing) => {
        if (!car.precio_mercado_promedio) return null;
        const diff = car.precio_mercado_promedio - car.precio;
        const percentDiff = ((diff / car.precio_mercado_promedio) * 100);
        const isFairPrice = Math.abs(percentDiff) <= 5;
        const isGoodPrice = diff > 0;

        if (isFairPrice) {
            return { label: 'Precio Justo', class: 'fair', diff: 0 };
        } else if (isGoodPrice) {
            return { label: 'Buen Precio', class: 'good', diff: Math.abs(Math.round(diff)) };
        } else {
            return { label: 'Sobre Mercado', class: 'high', diff: Math.abs(Math.round(diff)) };
        }
    };

    // Render the compact list item
    const renderListItem = (car: CarListing, index: number) => {
        const isSelected = selectedForComparison.includes(car.id_unico);
        const isActive = previewCar?.id_unico === car.id_unico;

        return (
            <div
                key={car.id_unico}
                className={`${styles.listItem} ${isActive ? styles.activeItem : ''} ${isSelected ? styles.selectedItem : ''}`}
                onClick={() => handleCarClick(car)}
                style={{ animationDelay: `${index * 0.03}s` }}
            >
                {/* Selection checkbox */}
                <div
                    className={styles.listItemCheckbox}
                    onClick={(e) => handleToggleSelection(car.id_unico, e)}
                >
                    <div className={`${styles.checkbox} ${isSelected ? styles.checked : ''}`}>
                        {isSelected && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </div>
                </div>

                {/* Thumbnail */}
                <div className={styles.listItemThumb}>
                    {car.imagen_url ? (
                        <img
                            src={car.imagen_url}
                            alt={`${car.marca} ${car.modelo}`}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className={styles.thumbPlaceholder}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 7.3 15 5 12 5s-6.7 2.3-8.5 6.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"
                                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                <circle cx="7" cy="17" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                <circle cx="17" cy="17" r="2" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className={styles.listItemInfo}>
                    <div className={styles.listItemTitle}>
                        {capitalizeFirstLetter(car.marca)} {capitalizeFirstLetter(car.modelo)}
                    </div>
                    <div className={styles.listItemMeta}>
                        <span>{car.fecha_fabricacion}</span>
                        <span className={styles.metaDot}>•</span>
                        <span>{(car.kilometraje || 0).toLocaleString()} km</span>
                    </div>
                </div>

                {/* Price */}
                <div className={styles.listItemPrice}>
                    <span className={styles.priceValue}>${(car.precio || 0).toLocaleString()}</span>
                    <span className={styles.savedTime}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        {formatSavedDate(car.savedAt)}
                    </span>
                </div>

                {/* Remove button */}
                <button
                    className={styles.listItemRemove}
                    onClick={(e) => handleRemoveCar(car.id_unico, e)}
                    disabled={isRemoving === car.id_unico}
                    aria-label="Eliminar"
                >
                    {isRemoving === car.id_unico ? (
                        <div className={styles.removeSpinner} />
                    ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    )}
                </button>

                {/* Active indicator */}
                {isActive && <div className={styles.activeIndicator} />}
            </div>
        );
    };

    // Render the preview panel content
    const renderPreviewContent = () => {
        if (!previewCar) {
            return (
                <div className={styles.previewEmpty}>
                    <div className={styles.previewEmptyIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <p>Selecciona un vehículo para ver detalles</p>
                </div>
            );
        }

        const priceIndicator = getPriceIndicator(previewCar);
        const carImages = getCarImages(previewCar);
        const validImages = carImages.filter(img => !imageError[img]);
        const currentImage = validImages[currentImageIndex] || null;
        const hasMultipleImages = validImages.length > 1;

        const handlePrevImage = (e: React.MouseEvent) => {
            e.stopPropagation();
            setCurrentImageIndex(prev => (prev - 1 + validImages.length) % validImages.length);
        };

        const handleNextImage = (e: React.MouseEvent) => {
            e.stopPropagation();
            setCurrentImageIndex(prev => (prev + 1) % validImages.length);
        };

        return (
            <div className={styles.previewContent}>
                {/* Mobile back button */}
                <button className={styles.mobileBackButton} onClick={handleMobileBack}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Volver a la lista</span>
                </button>

                {/* Image Gallery */}
                <div className={styles.imageGallery}>
                    <div className={styles.mainImageContainer}>
                        {currentImage ? (
                            <img
                                key={currentImage}
                                src={currentImage}
                                alt={`${previewCar.marca} ${previewCar.modelo}`}
                                className={styles.mainImage}
                                onError={() => handleImageError(currentImage)}
                            />
                        ) : (
                            <div className={styles.imagePlaceholder}>
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 7.3 15 5 12 5s-6.7 2.3-8.5 6.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"
                                          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    <circle cx="7" cy="17" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="17" cy="17" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                                <span>Sin imagen disponible</span>
                            </div>
                        )}

                        {/* Image navigation arrows */}
                        {hasMultipleImages && (
                            <>
                                <button className={`${styles.imageNavBtn} ${styles.prevBtn}`} onClick={handlePrevImage}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                <button className={`${styles.imageNavBtn} ${styles.nextBtn}`} onClick={handleNextImage}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Image counter */}
                        {validImages.length > 0 && (
                            <div className={styles.imageCounter}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                                    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>{currentImageIndex + 1} / {validImages.length}</span>
                            </div>
                        )}

                        <div className={styles.imageOverlay} />
                    </div>

                    {/* Thumbnail strip */}
                    {hasMultipleImages && (
                        <div className={styles.thumbnailStrip}>
                            {validImages.map((img, idx) => (
                                <button
                                    key={img}
                                    className={`${styles.thumbnail} ${idx === currentImageIndex ? styles.activeThumbnail : ''}`}
                                    onClick={() => setCurrentImageIndex(idx)}
                                >
                                    <img src={img} alt={`Vista ${idx + 1}`} onError={() => handleImageError(img)} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Car info */}
                <div className={styles.previewInfo}>
                    <div className={styles.previewHeader}>
                        <h2 className={styles.previewTitle}>
                            {previewCar.fecha_fabricacion} {capitalizeFirstLetter(previewCar.marca)} {capitalizeFirstLetter(previewCar.modelo)}
                        </h2>
                        {previewCar.version && previewCar.version.toUpperCase() !== 'SIN ESPECIFICAR' && (
                            <span className={styles.previewVersion}>{previewCar.version}</span>
                        )}
                    </div>

                    {/* Price section */}
                    <div className={styles.previewPriceSection}>
                        <div className={styles.previewPriceMain}>
                            <span className={styles.previewPrice}>${(previewCar.precio || 0).toLocaleString()}</span>
                            {Number(previewCar.rebaja) > 0 && (
                                <span className={styles.previewDiscount}>-{Number(previewCar.rebaja)}%</span>
                            )}
                        </div>
                        {priceIndicator && (
                            <div className={`${styles.previewPriceIndicator} ${styles[priceIndicator.class]}`}>
                                <div className={styles.indicatorIcon}>
                                    {priceIndicator.class === 'good' ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill="currentColor"/>
                                            <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : priceIndicator.class === 'fair' ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill="currentColor"/>
                                            <path d="M8 12h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill="currentColor"/>
                                            <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    )}
                                </div>
                                <span className={styles.indicatorLabel}>{priceIndicator.label}</span>
                                {priceIndicator.diff > 0 && (
                                    <span className={styles.indicatorDiff}>
                                        ${priceIndicator.diff.toLocaleString()} {priceIndicator.class === 'good' ? 'menos' : 'más'}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Specs grid */}
                    <div className={styles.previewSpecs}>
                        <div className={styles.specCard}>
                            <div className={styles.specIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M12 2v4m0 12v4m10-10h-4M6 12H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <div className={styles.specContent}>
                                <span className={styles.specLabel}>Kilometraje</span>
                                <span className={styles.specValue}>{(previewCar.kilometraje || 0).toLocaleString()} km</span>
                            </div>
                        </div>
                        <div className={styles.specCard}>
                            <div className={styles.specIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <rect x="2" y="6" width="20" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <path d="M6 10v4M10 8v8M14 9v6M18 7v10" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                            </div>
                            <div className={styles.specContent}>
                                <span className={styles.specLabel}>Transmisión</span>
                                <span className={styles.specValue}>{capitalizeFirstLetter(previewCar.transmision)}</span>
                            </div>
                        </div>
                        <div className={styles.specCard}>
                            <div className={styles.specIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                </svg>
                            </div>
                            <div className={styles.specContent}>
                                <span className={styles.specLabel}>Combustible</span>
                                <span className={styles.specValue}>{capitalizeFirstLetter(previewCar.tipo_combustible)}</span>
                            </div>
                        </div>
                        <div className={styles.specCard}>
                            <div className={styles.specIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                                          stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="12" cy="9" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                            </div>
                            <div className={styles.specContent}>
                                <span className={styles.specLabel}>Ubicación</span>
                                <span className={styles.specValue}>{capitalizeFirstLetter(previewCar.departamento || 'N/A')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Additional info */}
                    {previewCar.tipo_vendedor && (
                        <div className={styles.previewVendor}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>{capitalizeFirstLetter(previewCar.tipo_vendedor)}</span>
                        </div>
                    )}

                    {/* Transparency score */}
                    {previewCar.transparencia && (
                        <div className={styles.previewTransparency}>
                            <div className={styles.transparencyHeader}>
                                <span className={styles.transparencyLabel}>Transparencia</span>
                                <span className={styles.transparencyValue}>{previewCar.transparencia}%</span>
                            </div>
                            <div className={styles.transparencyBar}>
                                <div
                                    className={styles.transparencyFill}
                                    style={{ width: `${previewCar.transparencia}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className={styles.previewActions}>
                        <button className={styles.actionPrimary} onClick={handleViewFullDetails}>
                            <span>Ver detalles completos</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        {previewCar.url && (
                            <a
                                href={previewCar.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.actionSecondary}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M10 6H7c-1.11 0-2 .9-2 2v10c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2v-3M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Ver publicación</span>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {/* Animated background */}
            <div className={styles.backgroundGlow} />

            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerIcon}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"
                                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>Mis Favoritos</h1>
                        <p className={styles.subtitle}>Tu colección de vehículos guardados</p>
                    </div>
                </div>
                {onClose && (
                    <button className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                )}
            </div>

            {/* Stats Bar */}
            {!isLoading && savedCars.length > 0 && (
                <div className={styles.statsBar}>
                    <div className={styles.statItem}>
                        <span className={styles.statValue}>{savedCars.length}</span>
                        <span className={styles.statLabel}>{savedCars.length === 1 ? 'Vehículo' : 'Vehículos'}</span>
                    </div>
                    <div className={styles.statDivider} />
                    <div className={styles.statItem}>
                        <span className={styles.statValue}>${Math.round(avgPrice).toLocaleString()}</span>
                        <span className={styles.statLabel}>Precio Prom.</span>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className={styles.mainContent}>
                {isLoading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.loadingSpinner}>
                            <Spinner size={SpinnerSize.large} />
                        </div>
                        <Text className={styles.loadingText}>Cargando tu colección...</Text>
                    </div>
                ) : savedCars.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIllustration}>
                            <div className={styles.emptyCircle}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"
                                          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className={styles.emptyOrbit}>
                                <div className={styles.orbitDot} />
                            </div>
                        </div>
                        <h2 className={styles.emptyTitle}>Sin favoritos aún</h2>
                        <p className={styles.emptyDescription}>
                            Explora vehículos y guarda los que te interesen para acceder fácilmente después
                        </p>
                        <div className={styles.emptyHint}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>Toca el icono de guardado en cualquier vehículo</span>
                        </div>
                    </div>
                ) : (
                    <div className={`${styles.splitLayout} ${isMobilePreviewOpen ? styles.previewOpen : ''}`}>
                        {/* Left Panel - Car List */}
                        <div className={styles.listPanel}>
                            <div className={styles.listHeader}>
                                <span className={styles.listCount}>{savedCars.length} guardados</span>
                            </div>
                            <div className={styles.listScroll}>
                                {savedCars.map((car, index) => renderListItem(car, index))}
                            </div>
                        </div>

                        {/* Right Panel - Preview */}
                        <div className={styles.previewPanel}>
                            {renderPreviewContent()}
                        </div>
                    </div>
                )}
            </div>

            {/* Comparison FAB */}
            <ComparisonFAB
                selectedCount={selectedForComparison.length}
                onCompare={handleOpenComparison}
                onClear={handleClearSelection}
            />

            {/* Comparison View */}
            <CarComparisonView
                cars={selectedCarsData}
                isOpen={isComparisonOpen}
                onDismiss={() => setIsComparisonOpen(false)}
                onRemoveCar={handleRemoveFromComparison}
            />
        </div>
    );
};
